import { useRef, useState } from "react";
import axios from "axios";
import { API, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Info, Check, ChevronLeft } from "lucide-react";
import { CodeInput } from "@/lib/certidao";
import { makeForm, portalChunks } from "@/lib/upload";

const BLUE = "var(--btn)";
const linkCls = "block mx-auto text-sm text-slate-500 underline underline-offset-4 hover:text-slate-800";

function CodeScreen({ save, none, back }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="cert-code-screen">
      <button onClick={back} className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="cert-back"><ChevronLeft size={14} /> Voltar</button>
      <div><h3 className="text-lg font-bold text-slate-900">Código da certidão permanente</h3><p className="text-sm text-slate-500">São 12 números, no topo da certidão.</p></div>
      <CodeInput big onSave={save} testid="cert-code" label="Guardar código" />
      <p className="text-xs text-slate-500 -mt-1">Pode escrever com ou sem hífens.</p>
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3" aria-hidden="true" data-testid="cert-where">
        <div className="text-xs text-slate-500 mb-2">Onde encontrar:</div>
        <div className="h-2 w-2/3 bg-slate-200 rounded mb-2" />
        <div className="inline-block border-2 border-amber-400 rounded-md px-2 py-1 text-xs font-semibold text-amber-800">Código de acesso: 1234-5678-9012</div>
        <div className="h-2 w-5/6 bg-slate-200 rounded mt-2" /><div className="h-2 w-3/4 bg-slate-200 rounded mt-1.5" />
      </div>
      <button onClick={none} className={linkCls} data-testid="cert-code-none">Não encontro, a agência trata</button>
    </div>
  );
}

// «Uma pergunta» no portal do vendedor: nunca bloqueia nem conta como documento em falta.
export function CertidaoQuestion({ token, session, cert, onChange }) {
  const [view, setView] = useState("card");
  const fileRef = useRef(null);
  const headers = { "X-Portal-Session": session };
  const post = async (body) => {
    try { await axios.post(`${API}/portal/${token}/certidao`, body, { headers }); setView("card"); onChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const sendPdf = async (f) => {
    try {
      const fd = await makeForm([["file", f], ["key", "certidao_permanente"]], portalChunks(token, session));
      await axios.post(`${API}/portal/${token}/upload`, fd, { headers }); toast.success("Recebido, obrigado."); onChange();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  let body;
  if (view === "code") body = <CodeScreen save={(code) => post({ code })} none={() => post({ none: true })} back={() => setView("card")} />;
  else if (cert.code || cert.pdf) body = (
    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4" data-testid="cert-done">
      <div className="font-semibold text-emerald-800 flex items-center gap-1.5"><Check size={16} /> Certidão permanente</div>
      {cert.code && <div className="text-emerald-800" data-testid="cert-done-code">Código recebido: {cert.code}</div>}
      {!cert.code && <div className="text-emerald-800">PDF recebido.</div>}
      <p className="text-sm text-emerald-900/80 mt-0.5">Obrigado. A agência consulta a certidão online.</p>
      <button onClick={() => setView("code")} className={`${linkCls} mt-3`} data-testid="cert-change">Alterar</button>
    </div>
  );
  else if (cert.choice === "nao_tenho") body = (
    <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 flex gap-3 text-sm text-slate-700" data-testid="cert-agency">
      <Info size={18} className="text-sky-700 shrink-0 mt-0.5" />
      <span>Certidão permanente: <b>a agência trata disso.</b> Não precisa de fazer nada. <button onClick={() => setView("code")} className="underline text-slate-500" data-testid="cert-agency-change">Afinal tenho</button></span>
    </div>
  );
  else body = (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3 bg-white" data-testid="cert-question">
      <div><h3 className="font-bold text-slate-900">Tem a certidão permanente?</h3>
        <p className="text-sm text-slate-600 mt-0.5">Se já tiver uma, envie o PDF ou o código de acesso. Se não tiver, a agência trata disso.</p></div>
      <button onClick={() => setView("code")} className="w-full h-11 rounded-xl text-white font-semibold" style={{ background: BLUE }} data-testid="cert-insert-code">Inserir código</button>
      <button onClick={() => fileRef.current?.click()} className="w-full h-11 rounded-xl border border-slate-300 font-semibold text-slate-800 hover:bg-slate-50" data-testid="cert-send-pdf">Enviar PDF</button>
      <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) sendPdf(f); }} data-testid="cert-pdf-input" />
      <button onClick={() => post({ none: true })} className={linkCls} data-testid="cert-none">Não tenho, a agência trata</button>
    </div>
  );
  return (
    <div data-testid="portal-question">
      {body}
    </div>
  );
}
